import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { eventInside, onEscapeKey } from "../outsideDismiss";

export type MultiOption = { value: string; label: string };

/**
 * Themed multi-select dropdown (checkbox list). An empty selection means "all"
 * — the trigger reads "All <noun>" and no filter is applied by the caller.
 * Opaque `bf-surface` popover so nothing shows through; closes on outside-click
 * or Escape.
 */
export function MultiSelect({
  options,
  selected,
  onChange,
  allLabel,
  className = "",
  ariaLabel,
}: {
  options: MultiOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Text shown when nothing is selected, e.g. "All cameras". */
  allLabel: string;
  className?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let downOutside = false;
    const onDown = (e: PointerEvent) => {
      downOutside = !eventInside(e, ref.current);
    };
    const onUp = (e: PointerEvent) => {
      if (downOutside && !eventInside(e, ref.current)) setOpen(false);
    };
    const onKey = onEscapeKey(() => setOpen(false));
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const set = new Set(selected);
  const toggle = (value: string) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange([...next]);
  };

  const label =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? `1 selected`)
        : `${selected.length} selected`;

  return (
    <div ref={ref} className={"relative " + className}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-white/10 bg-bf-surface/60 py-1.5 pl-3 pr-2.5 text-sm font-medium text-bf-text transition hover:border-white/20"
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          size={15}
          className={"shrink-0 text-bf-muted transition " + (open ? "rotate-180" : "")}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute left-0 z-50 mt-1 max-h-72 w-full min-w-[13rem] overflow-auto rounded-lg border border-white/10 bg-bf-surface p-1 shadow-xl shadow-black/50"
        >
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mb-1 flex w-full items-center rounded-md px-3 py-1.5 text-left text-xs text-bf-muted transition hover:bg-white/5 hover:text-bf-text"
            >
              Clear selection
            </button>
          )}
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-bf-muted">Nothing to filter.</div>
          ) : (
            options.map((o) => {
              const on = set.has(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => toggle(o.value)}
                  className={
                    "flex w-full items-center justify-between gap-2 rounded-md px-3 py-1.5 text-left text-sm transition " +
                    (on ? "bg-bf-violet/20 text-white" : "text-bf-text hover:bg-white/5")
                  }
                >
                  <span className="truncate">{o.label}</span>
                  <span
                    className={
                      "grid h-4 w-4 shrink-0 place-items-center rounded border " +
                      (on ? "border-bf-violet bg-bf-violet/30" : "border-white/20")
                    }
                  >
                    {on && <Check size={11} className="text-white" />}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
